import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import ApiService, { NotFoundError } from './services/ApiService';
import UrlService from './services/UrlService';
import AuthorizationService from './services/AuthorizationService';
import DecryptionError from './services/DecryptionError';
import NotFoundScreen from './components/NotFoundScreen';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import LinkText from './components/LinkText';
import LanguageSwitcher from './components/LanguageSwitcher';
import { usePageMetadata } from './hooks/usePageMetadata';

function RankedElectionVotesList() {
    const { t } = useTranslation(['voting', 'common', 'election', 'validation']);
    const [rankedElectionVotes, setRankedElectionVotes] = useState([]);
    const [rankedElection, setRankedElection] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDecryptionError, setIsDecryptionError] = useState(false);
    const [isNotFoundError, setIsNotFoundError] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [voteToDelete, setVoteToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const { id } = useParams();
    const location = useLocation();
    
    // Page metadata
    const { title, description } = usePageMetadata(rankedElection);

    useEffect(() => {
        // Extract admin keys from URL
        UrlService.extractAndStoreAdminKeys(location, id);
        // Extract encryption keys from URL
        UrlService.extractAndStoreEncryptionKeys(location, id);

        setLoading(true);
        
        // Fetch both election details and votes using ApiService
        Promise.all([
            ApiService.getElection(id),
            ApiService.getVotes(id)
        ])
        .then(([electionData, votesData]) => {
            setRankedElection(electionData);
            setRankedElectionVotes(votesData);
            setLoading(false);
            setError(null);
            setIsDecryptionError(false);

            // Sync URL with correct encryption params from localStorage
            UrlService.syncEncryptionKeysWithUrl(id);
        })
        .catch(error => {
            console.error('Error fetching ranked election votes:', error);
            if (error instanceof NotFoundError) {
                setIsNotFoundError(true);
            } else if (error instanceof DecryptionError) {
                setError(t('common:errors.decryptionFailed'));
                setIsDecryptionError(true);
            } else {
                setError(t('voting:list.failedToLoadVotes'));
                setIsDecryptionError(false);
            }
            setLoading(false);
        });
    }, [id, location]); // eslint-disable-line react-hooks/exhaustive-deps


    const getVotePreview = (vote) => {
        if (!vote.options || vote.options.length === 0) {
            return t('voting:list.preview.noRankings');
        }
        
        // Sort by rank and get top 3
        const sortedOptions = [...vote.options].sort((a, b) => a.rank - b.rank);
        const topOptions = sortedOptions.slice(0, 3);
        
        // Use option names directly from the API response
        const optionNames = topOptions.map(voteOption => 
            voteOption.name || t('voting:list.preview.unknownOption')
        );
        
        if (optionNames.length === 0) return t('voting:list.preview.noRankings');
        if (optionNames.length === 1) return t('voting:list.preview.single', { first: optionNames[0] });
        if (optionNames.length === 2) return t('voting:list.preview.double', { first: optionNames[0], second: optionNames[1] });
        return t('voting:list.preview.triple', { first: optionNames[0], second: optionNames[1], third: optionNames[2] });
    };

    const handleDeleteClick = (e, vote) => {
        e.preventDefault(); // Prevent navigation to vote details
        e.stopPropagation();
        setVoteToDelete(vote);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!voteToDelete) return;
        
        setDeleteLoading(true);
        try {
            await ApiService.deleteVote(id, voteToDelete.id);
            
            // Remove vote from local state
            setRankedElectionVotes(prevVotes => 
                prevVotes.filter(vote => vote.id !== voteToDelete.id)
            );
            
            setSuccessMessage(t('voting:list.voteDeletedSuccess'));
            setTimeout(() => setSuccessMessage(null), 5000);
            
            setShowDeleteModal(false);
            setVoteToDelete(null);
        } catch (error) {
            console.error('Error deleting vote:', error);
            setError(error.message || t('validation:errors.failedToDeleteVote'));
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setVoteToDelete(null);
    };

    if (loading) {
        return (
            <div className="votes-list-container">
                <div className="container-fluid h-100">
                    <div className="row h-100 justify-content-center align-items-center">
                        <div className="col-12 text-center">
                            <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">{t('voting:states.loading')}</span>
                            </div>
                            <p className="text-light mt-3">{t('voting:list.loadingVotes')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show not found error screen if election was not found
    if (isNotFoundError) {
        return <NotFoundScreen type="election" />;
    }

    // Show decryption error screen if decryption failed
    if (isDecryptionError) {
        return (
            <div className="votes-list-container">
                <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                    <LanguageSwitcher />
                </div>

                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="votes-list-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">{t('common:errors.decryptionTitle')}</h4>
                                    <p>{t('common:errors.decryptionMessage')}</p>
                                    <hr />
                                    <p className="mb-0">{t('common:errors.decryptionAction')}</p>
                                </div>
                                <div className="mt-3">
                                    <Link to="/" className="btn btn-outline-secondary">
                                        {t('common:navigation.backToHome')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="votes-list-container">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="votes-list-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                                <div className="votes-list-nav">
                                    <Link to="/" className="btn btn-outline-secondary me-2">
                                        {t('voting:list.backToHome')}
                                    </Link>
                                    <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}`, id)} className="btn btn-outline-primary">
                                        {t('voting:list.backToElection')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <div className="votes-list-container">
            <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                <LanguageSwitcher />
            </div>
            
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-8">
                        {/* Navigation */}
                        <div className="votes-list-nav mb-4 pt-4">
                            <Link to="/" className="btn btn-outline-light btn-sm me-2">
                                {t('voting:list.backToHome')}
                            </Link>
                            <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}`, id)} className="btn btn-outline-light btn-sm">
                                {t('voting:list.backToElection')}
                            </Link>
                        </div>

                        {/* Success Message */}
                        {successMessage && (
                            <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
                                {successMessage}
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setSuccessMessage(null)}
                                ></button>
                            </div>
                        )}

                        {/* Election Context Card */}
                        <div className="votes-list-card mb-4">
                            <div className="votes-list-header">
                                <h1 className="votes-list-title">{t('voting:list.title')}</h1>
                                <div className="election-context">
                                    <h2 className="election-name">{rankedElection.title}</h2>
                                    {rankedElection.description && (
                                        <p className="election-description"><LinkText text={rankedElection.description} /></p>
                                    )}
                                    <div className="vote-summary">
                                        <span className="vote-count-badge">
                                            {t('voting:list.voteCount', { count: rankedElectionVotes.length })} {t('voting:list.votesCast')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Votes List */}
                        {rankedElectionVotes.length === 0 ? (
                            /* Empty State */
                            <div className="votes-list-card">
                                <div className="empty-state">
                                    <div className="empty-state-icon">🗳️</div>
                                    <h3>{t('voting:list.noVotesYet')}</h3>
                                    <p className="text-muted mb-4">
                                        {t('voting:list.beFirstToVote')}
                                    </p>
                                    <div className="empty-state-actions">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/create-vote`, id)}
                                            className="btn btn-primary btn-lg"
                                        >
                                            {t('voting:list.actions.castVote')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Votes Grid */
                            <div className="votes-list-card mb-4">
                                <div className="votes-grid-header">
                                    <h3>{t('voting:list.allVotes')}</h3>
                                    <p className="text-muted mb-0">{t('voting:list.clickToSeeDetails')}</p>
                                </div>
                                
                                <div className="votes-grid">
                                    {rankedElectionVotes.map((electionVote, index) => (
                                        <Link
                                            key={electionVote.id}
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/votes/${electionVote.id}`, id)}
                                            className="vote-card"
                                        >
                                            <div className="vote-action-buttons">
                                                {AuthorizationService.canEditVote(id, electionVote.id, rankedElection) && (
                                                    <Link
                                                        to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/votes/${electionVote.id}/update-vote`, id)}
                                                        className="btn btn-outline-warning btn-sm vote-edit-btn"
                                                        onClick={(e) => e.stopPropagation()}
                                                        title={t('voting:list.editVote')}
                                                    >
                                                        ✏️
                                                    </Link>
                                                )}
                                                {AuthorizationService.canDeleteVote(id, electionVote.id, rankedElection) && (
                                                    <button 
                                                        className="btn btn-outline-danger btn-sm vote-delete-btn"
                                                        onClick={(e) => handleDeleteClick(e, electionVote)}
                                                        title={t('voting:list.deleteVote')}
                                                    >
                                                        🗑️
                                                    </button>
                                                )}
                                            </div>
                                            <div className="vote-card-header">
                                                <div className="vote-number">
                                                    {electionVote.voterName ? electionVote.voterName : t('voting:list.voteNumber', { number: index + 1 })}
                                                </div>
                                            </div>
                                            <div className="vote-preview">
                                                {getVotePreview(electionVote)}
                                            </div>
                                            <div className="vote-card-footer">
                                                <span className="view-details">{t('voting:list.viewDetails')}</span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Actions Card */}
                        <div className="votes-list-card mb-4">
                            <div className="votes-actions-header">
                                <h3>{t('voting:list.actions.title')}</h3>
                            </div>
                            
                            <div className="votes-actions">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/create-vote`, id)}
                                            className="btn btn-primary btn-lg w-100 votes-action-btn"
                                        >
                                            {t('election:actions.castYourVote')}
                                        </Link>
                                    </div>
                                    <div className="col-md-6">
                                        <Link
                                            to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${id}/result`, id)}
                                            className="btn btn-success btn-lg w-100 votes-action-btn"
                                        >
                                            {t('election:actions.viewElectionResult')}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                voteDetails={voteToDelete}
                loading={deleteLoading}
            />
            </div>
        </>
    );
}

export default RankedElectionVotesList;